import { useState, useEffect } from "react";
import { ConfettiCanvas } from "@/components/ConfettiCanvas";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShoppingCart, Minus, Plus, Trash2, ExternalLink, Loader2, Truck, ShieldCheck, Heart, HandHeart, ArrowLeft, Info } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { useCartStore } from "@/stores/cartStore";
import { applyDiscountCodesToCart } from "@/lib/shopify";
import { UNIT_PRICE } from "@/lib/bundlePricing";
import { Input } from "@/components/ui/input";

import checkoutBg from "@/assets/checkout-bg-tunisian.webp";

const QUICK_AMOUNTS = [1, 2, 5]; 

export const CartDrawer = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [donationAmount, setDonationAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState("");
  const [nickname, setNickname] = useState("");
  const [showConfetti, setShowConfetti] = useState(false);
  const { items, isLoading, isSyncing, updateQuantity, removeItem, getCheckoutUrl, syncCart } = useCartStore();
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
  const eligibleStickerCount = items
    .filter((item) => item.productType === "sticker")
    .reduce((sum, item) => sum + item.quantity, 0);
  const eligibleStickerSubtotal = items
    .filter((item) => item.productType === "sticker")
    .reduce((sum, item) => sum + (parseFloat(item.price.amount) * item.quantity), 0);
  const nonBundleSubtotal = subtotal - eligibleStickerSubtotal;

  useEffect(() => { if (isOpen) syncCart(); }, [isOpen, syncCart]);

  const handleCheckout = async () => {
    const checkoutUrl = getCheckoutUrl();
    const { cartId } = useCartStore.getState();
    if (checkoutUrl && cartId) {
      let finalCheckoutUrl = checkoutUrl;

      try {
        // Clear any existing discount codes
        const result = await applyDiscountCodesToCart(cartId, []);
        if (result.checkoutUrl) finalCheckoutUrl = result.checkoutUrl;
      } catch (error) {
        console.error("Checkout discount apply failed:", error);
      }

      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
        window.location.href = finalCheckoutUrl;
        setDonationAmount(null);
        setCustomAmount("");
        setNickname("");
        setIsOpen(false);
      }, 1800);
    }
  };

  const handleQuickAmount = (amount: number) => {
    if (donationAmount === amount) {
      setDonationAmount(null);
      setCustomAmount("");
    } else {
      setDonationAmount(amount);
      setCustomAmount(amount.toString());
    }
  };

  const handleCustomAmountChange = (value: string) => {
    setCustomAmount(value);
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0 && num <= 1000) {
      setDonationAmount(num);
    } else if (value === "") {
      setDonationAmount(null);
    }
  };

  const totalPrice = subtotal + (donationAmount ?? 0);
  const freeShipping = totalPrice >= 59;

  return (
    <>
    <ConfettiCanvas active={showConfetti} />
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Panier" className="relative border-primary/30 hover:border-primary hover:bg-primary/5">
          <ShoppingCart className="h-5 w-5 text-primary" />
          {totalItems > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs bg-primary text-primary-foreground animate-in zoom-in duration-200">
              {totalItems}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md flex flex-col h-full p-0 border-l-0 overflow-hidden">
        <img src={checkoutBg} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 backdrop-blur-[2px]" />

        {/* Decorative top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[hsl(210,65%,38%)] via-primary to-[hsl(38,50%,78%)] z-20" />

        <div className="relative z-10 flex flex-col h-full">
          {/* Header - compact */}
          <div className="flex-shrink-0 px-5 py-4 border-b border-white/10">
            <SheetHeader>
              <SheetTitle className="font-display text-lg flex items-center gap-2 text-white">
                <Button variant="ghost" size="icon" className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10 -ml-1" onClick={() => setIsOpen(false)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <ShoppingCart className="w-4 h-4 text-[hsl(38,50%,78%)]" />
                Mon Panier
                <span className="ml-auto text-xs font-body text-white/40">🇹🇳</span>
              </SheetTitle>
              <SheetDescription className="font-body text-white/50 text-xs">
                {totalItems === 0 ? "Ton panier est vide ✨" : `${totalItems} article${totalItems !== 1 ? 's' : ''}`}
              </SheetDescription>
            </SheetHeader>
          </div>

          <div className="flex flex-col flex-1 px-4 pt-3 min-h-0">
            {items.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto">
                    <ShoppingCart className="h-8 w-8 text-white/30" />
                  </div>
                  <div>
                    <p className="text-white/70 font-body">Ton panier est vide</p>
                    <p className="text-xs text-white/40 mt-1">Découvre nos adhésifs tunisiens 🇹🇳</p>
                  </div>
                </div>
              </div>
            ) : (
              <>



                {/* Shipping bar - slim */}
                <div className="mb-3 px-3 py-2 rounded-lg bg-white/90 border border-white/20 shadow-md">
                  <div className="flex items-center gap-2">
                    <Truck className="w-3.5 h-3.5 text-primary shrink-0" />
                    <span className="text-[11px] font-body text-foreground flex-1">
                      {freeShipping
                        ? "✅ Livraison gratuite !"
                        : `Plus que ${(59 - totalPrice).toFixed(2)}$ pour la livraison gratuite`
                      }
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-1 overflow-hidden mt-1.5">
                    <div
                      className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${Math.min(100, (totalPrice / 59) * 100)}%` }}
                    />
                  </div>
                </div>

                {/* Items list - cleaner cards */}
                <div className="flex-1 overflow-y-auto pr-1 min-h-0 space-y-2">
                  {items.map((item) => {
                    const image = item.product.node.images?.edges?.[0]?.node;
                    const itemTotal = parseFloat(item.price.amount) * item.quantity;
                    return (
                      <div key={`${item.variantId}__${item.productType}`} className="flex gap-3 p-2.5 rounded-lg bg-white/90 border border-white/15 shadow-md hover:shadow-lg transition-shadow">
                        {/* Image */}
                        <div className="w-14 h-14 rounded-md overflow-hidden flex-shrink-0 border border-border/50">
                          {image ? (
                            <img src={image.url} alt={item.product.node.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <ShoppingCart className="w-5 h-5 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        {/* Details */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                          <div>
                            <h4 className="font-display font-semibold text-xs truncate text-foreground leading-tight">{item.product.node.title}</h4>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {item.productType === 'donation' ? '❤️ Don' : '🏷️ Adhésif'}
                              {item.selectedOptions.length > 0 && item.selectedOptions[0].value !== "Default Title" && (
                                <> · {item.selectedOptions.map(o => o.value).join(' · ')}</>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <div className="flex items-center bg-muted/80 rounded-md border border-border/50">
                              <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-destructive/10 hover:text-destructive rounded-md" onClick={() => updateQuantity(item.variantId, item.productType, item.quantity - 1)} disabled={isLoading}>
                                {item.quantity === 1 ? <Trash2 className="h-2.5 w-2.5" /> : <Minus className="h-2.5 w-2.5" />}
                              </Button>
                              <span className="w-6 text-center text-xs font-semibold text-foreground">{item.quantity}</span>
                              <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-primary/10 hover:text-primary rounded-md" onClick={() => updateQuantity(item.variantId, item.productType, item.quantity + 1)} disabled={isLoading}>
                                <Plus className="h-2.5 w-2.5" />
                              </Button>
                            </div>
                            <span className="font-display font-bold text-xs text-primary">
                              {itemTotal.toFixed(2)}$
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="flex-shrink-0 space-y-2.5 pt-3 border-t border-white/10 mt-3 pb-5">
                  {/* Donation - compact */}
                  <div className="rounded-lg bg-white/90 border border-white/15 shadow-md p-2.5">
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <HandHeart className="w-3 h-3 text-primary" />
                      <span className="font-display font-semibold text-[11px] text-foreground">Don solidaire 🇹🇳</span>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="ml-auto text-[9px] text-muted-foreground italic cursor-help flex items-center gap-0.5">
                              Non soumis aux rabais <Info className="w-2.5 h-2.5" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-[220px] text-xs">
                            Le montant du don est versé intégralement à la cause. Il n'est pas affecté par les codes promo ou réductions appliqués à votre commande.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <div className="flex gap-1 mb-1.5">
                      {QUICK_AMOUNTS.map((amount) => (
                        <button
                          key={amount}
                          onClick={() => handleQuickAmount(amount)}
                          className={`flex-1 py-1 rounded-md text-[11px] font-display font-bold transition-all duration-200 border ${
                            donationAmount === amount
                              ? "bg-primary text-primary-foreground border-primary shadow-sm"
                              : "bg-muted/50 text-foreground border-border hover:border-primary/40"
                          }`}
                        >
                          {amount}$
                        </button>
                      ))}
                      <Input
                        type="number"
                        min="0.5"
                        max="1000"
                        step="0.5"
                        placeholder="Autre"
                        value={customAmount}
                        onChange={(e) => handleCustomAmountChange(e.target.value)}
                        className="flex-1 h-[26px] text-[11px] bg-background border-border min-w-0 px-2"
                      />
                    </div>
                    {donationAmount && donationAmount > 0 && (
                      <div className="flex gap-1.5 items-center">
                        <Input
                          type="text"
                          placeholder="Pseudo (ou Anonyme)"
                          maxLength={50}
                          value={nickname}
                          onChange={(e) => setNickname(e.target.value)}
                          className="h-6 text-[11px] bg-background border-border flex-1 px-2"
                        />
                        <span className="text-[11px] text-primary font-body whitespace-nowrap flex items-center gap-0.5">
                          <Heart className="w-2.5 h-2.5 fill-primary" />{donationAmount.toFixed(0)}$ 💛
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Totals - clean */}
                  <div className="bg-white/90 rounded-lg p-3 shadow-md space-y-1.5">
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span className="font-body">Sous-total ({totalItems})</span>
                      <span className="text-foreground">{subtotal.toFixed(2)}$</span>
                    </div>
                    {donationAmount && (
                      <div className="flex justify-between items-center text-xs text-muted-foreground">
                        <span className="font-body flex items-center gap-1"><Heart className="w-2.5 h-2.5 text-primary fill-primary" /> Don</span>
                        <span className="text-primary font-semibold">+ {donationAmount.toFixed(2)}$</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                      <span className="font-body">Livraison</span>
                      <span className={freeShipping ? "text-accent font-semibold" : "text-foreground"}>
                        {freeShipping ? "Gratuite ✨" : "Au checkout"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-1.5 border-t border-border">
                      <span className="text-sm font-display font-bold text-foreground">Total</span>
                      <span className="text-lg font-display font-bold text-primary">
                        {totalPrice.toFixed(2)}$
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 font-display text-sm"
                    size="default"
                    disabled={items.length === 0 || isLoading || isSyncing}
                  >
                    {isLoading || isSyncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                        Passer la commande
                      </>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-3 text-[10px] text-white/50 pt-0.5">
                    <span className="flex items-center gap-1"><ShieldCheck className="w-2.5 h-2.5" /> Sécurisé</span>
                    <span className="text-[hsl(38,50%,78%)]">✦</span>
                    <span>🇨🇦 Canada</span>
                    <span className="text-[hsl(38,50%,78%)]">✦</span>
                    <span>🇹🇳 Tunisie</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
    </>
  );
};
